// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import { convertToLocaleId, handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import client from '@Client';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { THList } from '@Molecules/THList';
import { useCurrentUser } from '@Queries/userQueries';
import usePermissions from '@Hooks/usePermissions';
import { AdminPermissionsEnum, CustomStatusMapType, CustomStatusOutput, ObjectTypeV1 as ObjectType } from 'thehive-sdk';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { computeEventList } from '@Organisms/CustomStatus/eventList';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { useListAllFields } from '@Queries/describeQueries';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import useConfigList from '@Hooks/useConfigList';
import { CustomStatusManage } from '@Organisms/CustomStatus/CustomStatusManage/CustomStatusManage';
import { ICONS } from '@Constants/iconsApp';
import { StatusContext } from '@Context/StatusProvider';
import { computeListSelector } from '@Organisms/CustomStatus/selector';
import { useDrawer } from '@Hooks/useDrawer';
import usePageTitle from '@Hooks/usePageTitle';

import './AlertStatusList.scss';

const AlertStatusList = (): ReactElement => {
    const i = useLocalMessage();

    const { licenseCapabilities } = useContext(StatusContext);
    const { visible: visibleEdit, setVisible: setVisibleEdit, data, setData } = useDrawer<CustomStatusOutput>({});
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer({});

    const customStatusCapabilities = useMemo(
        () => !!licenseCapabilities?.alert?.customStatus || false,
        [licenseCapabilities],
    );

    const { data: describe } = useListAllFields();
    const [alertStatus, setAlertStatus] = React.useState<CustomStatusMapType>({});

    const config = useConfigList<CustomStatusOutput>({
        selector: computeListSelector({ entity: ObjectType.ALERT }),
        controller: client.CustomStatusController,
        storageId: StorageFilterEnum.ALERT_STATUS,
        objectType: ObjectType.ALERT_STATUS,
    });

    const { listRefetch, dataSource = [], columnsRenderer, status = 'loading' } = config;

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CustomStatusController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
                onError: handleNotificationError,
            }),
        [i, listRefetch],
    );

    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const hasPermissions = usePermissions({
        permissionsNeeded: [AdminPermissionsEnum.MANAGE_CONFIG],
        permissionsList: permissions,
    });

    const columns = columnsRenderer([
        {
            className: 'alert-status-list__column__colour',

            render: ({ colour }) => (
                <span className="alert-status__colour" style={{ ['--color' as string]: colour }}>
                    &nbsp;
                </span>
            ),
        },
        {
            title: [{ label: i('custom-status.list.status'), sortField: 'value' }],
            className: 'alert-status-list__column__value',
            render: ({ value }) => (
                <span className="alert-status__value">
                    {i({
                        id: `alert.data.status.${convertToLocaleId(value)}`,
                        defaultMessage: value,
                    })}
                </span>
            ),
        },
        {
            title: [{ label: i('custom-status.list.stage'), sortField: 'stage' }],
            className: 'alert-status-list__column__stage',
            render: ({ stage }) => <span className="alert-status__value">{stage}</span>,
        },
        {
            className: 'alert-status-list__column__actions',
            hidden: !customStatusCapabilities,
            render: (data) =>
                hasPermissions ? (
                    <ActionsList
                        actionList={{
                            edit: {
                                label: i('general.edit'),
                                icon: <ICONS.EditIcon />,
                                query: () => {
                                    setData(data);
                                    setVisibleEdit(true);
                                },
                            },
                            delete: {
                                label: i('general.delete'),
                                confirm: true,
                                message: i('custom-status.list.delete'),
                                query: () => eventList.alert.onDelete(data._id),
                                icon: <ICONS.DeleteIcon />,
                                disabled: dataSource.length <= 1 || !data.extraData?.canDelete,
                            },
                        }}
                        isIcon
                        scope="single"
                    />
                ) : null,
        },
    ]);

    useEffect(() => {
        if (dataSource.length) {
            const alertStatusMap = dataSource.reduce<CustomStatusMapType>((acc, curr) => {
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setAlertStatus(alertStatusMap);
        }
    }, [dataSource]);

    usePageTitle(i('page-title.admin.entities.alert-status'));

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && (
                            <AddButton
                                label={i('custom-status.create.label')}
                                disabled={!customStatusCapabilities}
                                onClick={() => {
                                    setVisibleCreate(true);
                                }}
                            />
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    className="alert-status-list__wrapper"
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('customField.none')}>
                            {hasPermissions && (
                                <THButton
                                    type="link"
                                    onClick={() => {
                                        setVisibleCreate(true);
                                    }}
                                >
                                    {i('custom-status.create.label')}
                                </THButton>
                            )}
                        </DisplayEmptyData>
                    }
                />
            </THList>
            {data && visibleEdit && (
                <CustomStatusManage
                    describeField={describe?.[ObjectType.ALERT]}
                    onFinish={(value) => eventList.alert.onEdit(value, data._id)}
                    data={data}
                    visible={visibleEdit}
                    onVisibleChange={setVisibleEdit}
                />
            )}
            {visibleCreate && (
                <CustomStatusManage
                    describeField={describe?.[ObjectType.ALERT]}
                    onFinish={eventList.alert.onCreate}
                    visible={visibleCreate}
                    statusList={alertStatus}
                    onVisibleChange={setVisibleCreate}
                />
            )}
        </>
    );
};

const path = RouteBuilder.compute('administration.alert-status');

export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.alert-status',
        icon: <ICONS.AlertIcon />,
    },
    breadcrumb: {
        label: 'breadcrumb.global.alert-status',
    },
    content: <AlertStatusList />,
    permissionNeeded: [AdminPermissionsEnum.MANAGE_CONFIG],
    space: ['admin'],
    exact: true,
};
